import React, { createContext, useEffect, useState, useRef } from 'react';
import {
  Container,
  makeStyles,
  useTheme,
  useMediaQuery,
  AppBar
} from '@material-ui/core';
import FooterContainer from '@components/Footer';
import {
  HomeMain,
  Capabilities,
  PartnerIntro,
  HowToUse,
  Reviews,
  Clients,
  Blog,
  NewsLetterSubscription,
  CapaNews,
  CapaQuestion,
  TechinsideModal
} from '@routes/Home/Components';
import ErrorNoticePresenter from '@components/Dialog/Notice/ErrorNoticePresenter';
import { CustomizedButton } from '@components/Customized';
import { LOGO_MEDIUM, LOGO_MEDIUM_WHITE } from '@assets';
import { useHistory } from 'react-router-dom';
import { colorSet } from '@components/Provider';
import GnbCheckerBoardMenu from '@routes/Common/CommonLayout/GnbCheckerBoardMenu';
import useClient from '@hooks/useClient';
import NotificationContainer from '@components/Notification';
import CommonLayout from '@routes/Common/CommonLayout';
import BottomNav from '@routes/Common/BottomNav';

export const MediaQuery = createContext({
  md: false,
  sm: false
});

const HomePresenter: React.FC = () => {
  const theme = useTheme();
  const isTarget = useRef<HTMLDivElement>(null);
  const matchesMd = useMediaQuery(theme.breakpoints.down('md'));
  const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [barState, setBarState] = useState<boolean>(false);
  const { container, homeMainContainer, mobileAppBar } = useStyles({
    currentScroll: barState
  });
  const history = useHistory();
  const { clientState } = useClient();
  const isAuth = Boolean(clientState?.accessToken);

  useEffect(() => {
    const observer = new IntersectionObserver(callBackFunction, options);
    const target = isTarget.current;

    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  });

  const callBackFunction = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    setBarState(!entry.isIntersecting);
  };

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0
  };

  return (
    <div className={container}>
      <CommonLayout gnbTabs={isAuth ? null : <></>}>
        {matchesMd && (
          <AppBar className={mobileAppBar} elevation={0}>
            <CustomizedButton
              className="logoBtn"
              variant="text"
              onClick={() => history.push('/')}
            ></CustomizedButton>
            <div>
              <CustomizedButton
                className="gnb-blog"
                variant="text"
                onClick={() => {
                  history.push('/blog');
                }}
              >
                블로그
              </CustomizedButton>
              <GnbCheckerBoardMenu />
              {isAuth ? (
                <NotificationContainer />
              ) : (
                <CustomizedButton
                  variant="contained"
                  onClick={() => history.push('/login')}
                  size="small"
                  className="loginButton"
                >
                  로그인
                </CustomizedButton>
              )}
            </div>
          </AppBar>
        )}
        <MediaQuery.Provider value={{ md: matchesMd, sm: matchesSm }}>
          <ErrorNoticePresenter />
          <Container
            className={homeMainContainer}
            component="main"
            disableGutters
          >
            <div ref={isTarget}></div>
            <TechinsideModal />
            <HomeMain />
            <Clients />
            <Capabilities />
            <Reviews />
            <HowToUse />
            <Blog />
            <PartnerIntro />
            <CapaNews />
            <NewsLetterSubscription />
            <CapaQuestion />
          </Container>
        </MediaQuery.Provider>
        <FooterContainer />
        {isAuth && <BottomNav />}
      </CommonLayout>
    </div>
  );
};

export default HomePresenter;

const useStyles = makeStyles(() => ({
  container: (props: { currentScroll: boolean }) => ({
    '& .tab-wrapper': {
      '& p': {
        color: `${props.currentScroll ? colorSet.gray900 : colorSet.gray000}`
      }
    },
    '& .contentHeaderMargin': {
      display: 'none'
    },
    '& .gnb-desktop-logo': {
      position: 'fixed',
      background: `${props.currentScroll ? colorSet.gray000 : 'none'}`,
      '& .capaBackground': {
        width: '90px',
        height: '20px',
        boxSizing: 'border-box'
      },
      '& .capaLogo': {
        display: 'none'
      },
      '& .desktop-logo': {
        minWidth: 77,
        minHeight: 20,
        height: 20,
        background: `${
          props.currentScroll
            ? `url(${LOGO_MEDIUM}) no-repeat`
            : `url(${LOGO_MEDIUM_WHITE}) no-repeat`
        }`,
        backgroundSize: 'contain',
        transition: '0.3s ease-in-out'
      },
      '& .gnb-checkerboard-menu': {
        color: `${props.currentScroll ? colorSet.gray900 : colorSet.gray000}`,
        '&:hover': {
          color: colorSet.gray900
        },
        transition: '0.3s ease-in-out'
      },
      borderBottom: `${
        props.currentScroll
          ? `1px solid ${colorSet.gray400}`
          : '1px solid rgba(0,0,0,0)'
      }`,
      transition: '0.3s ease-in-out'
    },
    '& .gnb-notification-icon': {
      '& span > svg': {
        color: `${props.currentScroll ? colorSet.gray900 : colorSet.gray000}`,
        transition: '0.3s ease-in-out'
      },
      '&:hover': {
        '& span > svg': {
          color: colorSet.gray900
        }
      },
      transition: '0.3s ease-in-out'
    }
  }),
  homeMainContainer: {
    maxWidth: 'unset',
    overflow: 'hidden'
  },
  mobileAppBar: (props: { currentScroll: boolean }) => ({
    background: `${props.currentScroll ? colorSet.gray000 : 'none'}`,
    height: 48,
    position: 'fixed',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 16px',
    borderBottom: `${
      props.currentScroll
        ? `1px solid ${colorSet.gray400}`
        : '1px solid rgba(0,0,0,0)'
    }`,
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: '14px'
    },
    '& .gnb-checkerboard-menu': {
      color: `${props.currentScroll ? colorSet.gray900 : colorSet.gray000}`,
      '&:hover': {
        color: colorSet.gray900
      },
      transition: '0.3s ease-in-out'
    },
    '& .gnb-notification-icon': {
      '& span > svg': {
        color: `${props.currentScroll ? colorSet.gray900 : colorSet.gray000}`,
        transition: '0.3s ease-in-out'
      },
      '&:hover': {
        '& span > svg': {
          color: colorSet.gray900
        }
      },
      transition: '0.3s ease-in-out'
    },
    '& .logoBtn': {
      minWidth: 65,
      minHeight: 18,
      background: `${
        props.currentScroll
          ? `url(${LOGO_MEDIUM}) no-repeat`
          : `url(${LOGO_MEDIUM_WHITE}) no-repeat`
      }`,
      backgroundSize: 'contain',
      transition: '0.3s ease-in-out'
    },
    '& .loginButton': {
      backgroundColor: `${
        props.currentScroll ? colorSet.gray200 : colorSet.gray000
      }`,
      color: colorSet.gray900
    },
    '& .gnb-blog': {
      color: colorSet.gray700
    },
    transition: '0.3s ease-in-out'
  })
}));
